/**
 * Mise en page du texte pour l'image partagée — logique pure, sans canvas.
 *
 * Toutes les fonctions reçoivent une fonction de mesure injectée
 * `(texte, taille) => largeur en px`. En production c'est un `ctx.measureText`,
 * en test c'est une mesure synthétique déterministe. On peut donc tester le
 * retour à la ligne, l'ajustement de taille et la troncature sans DOM.
 */

export type Measurer = (text: string, fontSize: number) => number;

export const ELLIPSIS = '…';

/** Retire espaces et ponctuation en fin de chaîne, pour ne pas obtenir « mot.… ». */
export function stripTrailingPunctuation(text: string): string {
  return text.replace(/[\s.,;:!?…·—–-]+$/u, '');
}

function dropLastCodePoint(text: string): string {
  const points = Array.from(text);
  points.pop();
  return points.join('');
}

/**
 * Casse un mot unique trop long pour une ligne en morceaux tenant chacun dans
 * `maxWidth` (découpe au code point, gère les accents/emoji). Sans ce garde-fou
 * un mot à rallonge déborderait silencieusement.
 */
export function breakLongWord(word: string, maxWidth: number, fontSize: number, measure: Measurer): string[] {
  const pieces: string[] = [];
  let piece = '';
  for (const ch of word) {
    const candidate = piece + ch;
    if (piece && measure(candidate, fontSize) > maxWidth) {
      pieces.push(piece);
      piece = ch;
    } else {
      piece = candidate;
    }
  }
  if (piece) pieces.push(piece);
  return pieces;
}

/**
 * Découpe `text` en lignes tenant chacune dans `maxWidth` à la taille donnée.
 * Coupe en priorité sur les espaces ; un mot plus long qu'une ligne est cassé.
 */
export function wrapText(text: string, maxWidth: number, fontSize: number, measure: Measurer): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  const place = (word: string): void => {
    if (!current) {
      if (measure(word, fontSize) <= maxWidth) {
        current = word;
        return;
      }
      const pieces = breakLongWord(word, maxWidth, fontSize, measure);
      current = pieces.pop() ?? '';
      lines.push(...pieces);
      return;
    }
    const candidate = `${current} ${word}`;
    if (measure(candidate, fontSize) <= maxWidth) {
      current = candidate;
      return;
    }
    lines.push(current);
    current = '';
    place(word);
  };

  for (const word of words) place(word);
  if (current) lines.push(current);
  return lines;
}

/**
 * Tronque un texte pour qu'il tienne (ellipse comprise) dans `maxWidth`,
 * en retirant la ponctuation finale avant d'ajouter « … ».
 */
export function ellipsize(text: string, maxWidth: number, fontSize: number, measure: Measurer): string {
  let s = stripTrailingPunctuation(text);
  while (s.length > 0 && measure(`${s}${ELLIPSIS}`, fontSize) > maxWidth) {
    s = stripTrailingPunctuation(dropLastCodePoint(s));
  }
  return `${s}${ELLIPSIS}`;
}

/**
 * Limite le nombre de lignes ; la dernière ligne conservée reçoit l'ellipse
 * quand du contenu a été coupé.
 */
export function clampLines(
  lines: string[],
  maxLines: number,
  maxWidth: number,
  fontSize: number,
  measure: Measurer,
): string[] {
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  const lastIndex = maxLines - 1;
  kept[lastIndex] = ellipsize(kept[lastIndex], maxWidth, fontSize, measure);
  return kept;
}

export interface TitleLayout {
  fontSize: number;
  lines: string[];
}

/**
 * Choisit la plus grande taille (parmi `sizes`) pour laquelle le titre tient
 * en au plus `maxLines` lignes de `maxWidth`. Si même la plus petite déborde,
 * on garde la plus petite et on tronque proprement.
 */
export function layoutTitle(
  text: string,
  opts: { maxWidth: number; maxLines: number; sizes: number[]; measure: Measurer },
): TitleLayout {
  const { maxWidth, maxLines, sizes, measure } = opts;
  const descending = [...sizes].sort((a, b) => b - a);

  for (const fontSize of descending) {
    const lines = wrapText(text, maxWidth, fontSize, measure);
    if (lines.length <= maxLines) return { fontSize, lines };
  }

  const fontSize = descending[descending.length - 1];
  const lines = clampLines(wrapText(text, maxWidth, fontSize, measure), maxLines, maxWidth, fontSize, measure);
  return { fontSize, lines };
}
